import { useState } from 'react';

import Modal from '../../common/Modal';
import {
  DestructiveButton,
  DialogCard,
  DialogFooter,
  DialogText,
  DialogTitle,
  SecondaryButton,
} from './folderDialogStyles';

import type { Folder } from '@src/types.ts/story';
import type { FC } from 'react';

type Props = {
  folder: Folder;
  subfolderCount: number;
  storyCount: number;
  onConfirm: (mode: 'cascade' | 'promote') => Promise<void> | void;
  onClose: () => void;
};

const DeleteFolderDialog: FC<Props> = ({
  folder,
  subfolderCount,
  storyCount,
  onConfirm,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);
  const isEmpty = subfolderCount === 0 && storyCount === 0;
  // "Move contents up" only makes sense when there are stories to move.
  // A folder containing only subfolders shows the simpler 2-button confirm.
  const showPromoteOption = storyCount > 0;

  const run = async (mode: 'cascade' | 'promote') => {
    setBusy(true);
    try {
      await onConfirm(mode);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen closeModal={onClose}>
      <DialogCard $size="md" data-testid="delete-folder-dialog">
        <DialogTitle>Delete &ldquo;{folder.name}&rdquo;?</DialogTitle>
        <DialogText>
          {isEmpty
            ? 'This folder is empty. This action cannot be undone.'
            : showPromoteOption
              ? `This folder contains ${subfolderCount} ${
                  subfolderCount === 1 ? 'subfolder' : 'subfolders'
                } and ${storyCount} ${
                  storyCount === 1 ? 'story' : 'stories'
                }. Choose what should happen to its contents.`
              : `This folder contains ${subfolderCount} ${
                  subfolderCount === 1 ? 'subfolder' : 'subfolders'
                } but no stories. Deleting will remove the folder and its subfolders.`}
        </DialogText>
        {isEmpty || !showPromoteOption ? (
          <DialogFooter>
            <SecondaryButton
              type="button"
              disabled={busy}
              onClick={onClose}
              data-testid="delete-folder-cancel"
            >
              Cancel
            </SecondaryButton>
            <DestructiveButton
              type="button"
              disabled={busy}
              onClick={() => void run('cascade')}
              data-testid="delete-folder-confirm-empty"
            >
              Delete
            </DestructiveButton>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <SecondaryButton
              type="button"
              disabled={busy}
              onClick={onClose}
              data-testid="delete-folder-cancel"
            >
              Cancel
            </SecondaryButton>
            <SecondaryButton
              type="button"
              disabled={busy}
              onClick={() => void run('promote')}
              data-testid="delete-folder-promote"
            >
              Move Contents Up
            </SecondaryButton>
            <DestructiveButton
              type="button"
              disabled={busy}
              onClick={() => void run('cascade')}
              data-testid="delete-folder-cascade"
            >
              Delete Everything
            </DestructiveButton>
          </DialogFooter>
        )}
      </DialogCard>
    </Modal>
  );
};

export default DeleteFolderDialog;
